import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Testing Backbone apps with Mocha",
  "description": "",
  "published": "2012-12-27T00:00:00.000Z",
  "redirect_from": ["/blog/testing-backbone-apps-with-mocha/swizec/5788"],
  "categories": "Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "http://swizec.com/blog/wp-content/uploads/2012/12/Selection_090.png"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2012/12/Selection_090.png",
          "alt": "A test bed",
          "title": "A test bed"
        }}></img></a></p>
    <p>{`A while ago I decided it was finally time to bite the bullet and learn frontend `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Software_testing",
        "title": "Software testing"
      }}>{`testing`}</a>{`. Things were getting out of hand with a project I'm working on (`}<a parentName="p" {...{
        "href": "http://melt.ee"
      }}>{`melt.ee`}</a>{`) and I could no longer keep a good development pace while ensuring a good level of quality.`}</p>
    <p>{`You know the drill, it's just a three page app. Then `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Corner_case",
        "title": "Corner case"
      }}>{`corner cases`}</a>{` in UX start cropping up and suddenly it's a 7 page app whose state machine covers a conference room whiteboard. Suddenly your approach produces three bugs for every one you fix and the thought of adding features gives you nightmares.`}</p>
    <p>{`Time for a refactor!`}</p>
    <p>{`And a brilliant opportunity to introduce some tests.`}</p>
    <h2 {...{
      "id": "mocha-frontend-testing"
    }}>{`Mocha frontend testing`}</h2>
    <p>{`Looking far and wide for a nice tool to use I finally settled on `}<a parentName="p" {...{
        "href": "http://mochajs.com"
      }}>{`Mocha`}</a>{` because it's the same set of tools I use for testing node.js servers. Selenium still feels kind of weird to me and although `}<a parentName="p" {...{
        "href": "http://casperjs.org/"
      }}>{`Casper.js`}</a>{` looks awesome I had a hard time figuring out how to begin using it, plus it didn't feel like the perfect fit.`}</p>
    <lite-youtube {...{
      "videoid": "zvvUN2L5UnA",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Mocha is a pure javascript test runner that simply lets you run some javascript code. That's it. Because I was testing a Backbone.js app it was the best fit - all I really have to do is run some of my production code and check for side-effects.`}</p>
    <p>{`I'm running my tests in the browser because for the life of me I couldn't get `}<a parentName="p" {...{
        "href": "https://github.com/metaskills/mocha-phantomjs"
      }}>{`mocha-phantomjs`}</a>{` to run the tests even though they seem to work fine. No output at all.`}</p>
    <p>{`Most of my tests merely check that views are rendering correctly so they look something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`describe("Events", function () {
  beforeEach(function () {
    App.state.clear({ silent: true });
    $("div#view").html("");
  });

  it("renders go button", function () {
    App.state.set({ page: "events" });

    var $go = $("div#view a.go:visible");
    $go.size().should.equal(1);
    $go.attr("href").should.equal("/lobby");
  });
});
`}</code></pre>
    <p>{`As you can see, my testing harness has a `}{`_`}{`div#view `}{`_`}{`where things render, then I tell the App to go into a certain state and check the correct view has rendered.`}</p>
    <p>{`This is where things get tricky and I have yet to fully figure them out.`}</p>
    <p>{`I want to test the actions the App takes. Does clicking a button perform the right actions? Does it react correctly to changes in the url? Stuff like that.`}</p>
    <p>{`Problem is, triggering those actions would make the browser navigate away from the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Testbed",
        "title": "Testbed"
      }}>{`test bed`}</a>{` and that's not what I want. There's already enough issues with the App reacting to some events by pushing url changes to the browser so I can't just blindly refresh the test page.`}</p>
    <p>{`Another interesting source of problems is that in some states the App must perform a redirect - sometimes you want to run the tests, but are greeted with a `}<em parentName="p">{`"Oh hey, let me just redirect that to X!"`}</em></p>
    <p>{`I hear Casper.js is a better fit for making full navigation tests, so that's the next thing I'll be looking into.`}</p>
    <p>{`Despite the failings of my approach, frontend testing has already been a great help with this project. No longer must I spend five minutes clicking around to see changes in a view that's 5 steps down the funnel, I have a reasonable first indicator of problems when dependencies bleed over ...`}</p>
    <p>{`... hell, it's even caught a backend bug that I didn't anticipate in the other `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Test_suite",
        "title": "Test suite"
      }}>{`test suite`}</a>{`. Scoar!`}</p>
    <p>{`Most of all, it makes development more pleasurable. Seeing those views fly by when the tests run brings a smile to my face in pure nerdy joy.`}</p>
    <p>{`That's important.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/testing-socket-io-apps/swizec/5625"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/129148469_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://swizec.com/blog/testing-socket-io-apps/swizec/5625"
        }}>{`Testing socket.io apps`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://dailyjs.com/2012/12/06/backbone-tutorial-2/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_68_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://dailyjs.com/2012/12/06/backbone-tutorial-2/"
        }}>{`Backbone.js Tutorial: Google's APIs and RequireJS`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://blog.shinetech.com/2012/12/24/an-identity-map-for-backbone-js/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/134104481_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://blog.shinetech.com/2012/12/24/an-identity-map-for-backbone-js/"
        }}>{`An Identity Map for Backbone.js | The Shine Technologies Blog`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://japhr.blogspot.com/2012/12/more-mock-http-requests-in-dart.html"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/134197046_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://japhr.blogspot.com/2012/12/more-mock-http-requests-in-dart.html"
        }}>{`More Mock Http Requests in Dart`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.joezimjs.com/javascript/tips-for-front-end-unit-testing/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/134107575_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.joezimjs.com/javascript/tips-for-front-end-unit-testing/"
        }}>{`Tips for Front End Unit Testing`}</a></li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=066807a4-33f4-4024-b1d1-c7cb923d86af",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      